import { priceInputPence } from './priceInput';
import type { JobPricing, PriceBreakdown } from '../types/models';

export interface ChargeDraft {
  id: string; code: string; description: string; quantity: string; amount: string; flagId?: string | null;
}
export const MAX_CHARGES = 20;
export function chargeDrafts(pricing?: JobPricing | null): ChargeDraft[] {
  return (pricing?.breakdown?.charges ?? []).map(line => ({id: line.id, code: line.code ?? '', description: line.description ?? '',
    quantity: line.quantity == null ? '' : String(line.quantity), amount: (line.amountPence / 100).toFixed(2), flagId: line.flagId}));
}
export function quantityValue(text: string): number | null {
  if (!text.trim()) return null;
  const number = Number(text);
  if (!/^\d+(?:\.\d{1,2})?$/.test(text.trim()) || !Number.isFinite(number) || number > 100_000 || Math.round(number * 100) !== number * 100) {
    throw new Error('Quantity must be from 0 to 100000 with at most 2 decimal places.');
  }
  return number;
}
function pence(text: string): number {
  const value = priceInputPence(text);
  if (value === null || !Number.isSafeInteger(value) || value > 100_000_000) throw new Error('Enter a valid price.');
  return value;
}
/** New clients always preserve a breakdown or explicitly clear it. */
export function priceDraftPayload(total: string, useBreakdown: boolean, jobAmount: string, charges: ChargeDraft[]): {amountPence: number; breakdown: PriceBreakdown | null} {
  if (!useBreakdown) return {amountPence: pence(total), breakdown: null};
  if (charges.length > MAX_CHARGES) throw new Error('Additional charges must have at most 20 lines.');
  const jobAmountPence = pence(jobAmount);
  const ids = new Set<string>();
  const flags = new Set<string>();
  const lines = charges.map(line => {
    if (!/^[A-Za-z0-9_-]{1,64}$/.test(line.id) || ids.has(line.id)) throw new Error('Each additional charge needs its own valid ID.');
    ids.add(line.id);
    const code = line.code.trim().toUpperCase();
    const description = line.description.trim();
    if ((!code && !description) || code.length > 8 || (code && !/^[A-Z0-9]+$/.test(code)) || description.length > 200) {
      throw new Error('Each additional charge needs a code (up to 8 letters or digits) or a description (up to 200 characters).');
    }
    if (line.flagId) {
      if (!/^[A-Za-z0-9_-]{1,64}$/.test(line.flagId) || flags.has(line.flagId)) throw new Error('A driver flag can only be priced once.');
      flags.add(line.flagId);
    }
    return {id: line.id, code: code || null, description: description || null, quantity: quantityValue(line.quantity),
      amountPence: pence(line.amount), flagId: line.flagId || null};
  });
  const amountPence = lines.reduce((sum, line) => sum + line.amountPence, jobAmountPence);
  if (amountPence > 100_000_000) throw new Error('The total cannot exceed £1,000,000.');
  return {amountPence, breakdown: lines.length ? {jobAmountPence, charges: lines} : null};
}
